import { NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    InputSignal,
    PLATFORM_ID,
    Signal,
    TemplateRef,
    afterNextRender,
    computed,
    inject,
    input,
    viewChild,
} from '@angular/core';

import { BlockDirective, ElemDirective, ModDirective } from '@rt-tools/core';
import { RtButtonDirective } from '@rt-tools/ui-kit-v2';

const DEFAULT_AUTOPLAY_MS: number = 3000;
const MIN_AUTOPLAY_MS: number = 1000;
const SCROLL_SETTLE_MS: number = 120;
const BEM_BLOCK: string = 'sc-card-carousel';

@Component({
    selector: 'sc-card-carousel',
    templateUrl: './card-carousel.component.html',
    styleUrl: './card-carousel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        NgTemplateOutlet,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,

        // rt-tools ui-kit
        RtButtonDirective,
    ],
    host: {
        class: BEM_BLOCK,
    },
})
export class CardCarouselComponent<T> {
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);

    #autoplayHandle: ReturnType<typeof setInterval> | null = null;
    #scrollSettleHandle: ReturnType<typeof setTimeout> | null = null;
    #hovered: boolean = false;
    #focused: boolean = false;

    // Re-align the track once scrolling settles. The list is rendered twice, so
    // shifting by exactly one copy width swaps a position in the second copy for
    // the identical position in the first — invisible, which keeps the loop seamless.
    readonly #onScroll: () => void = (): void => {
        if (this.#scrollSettleHandle !== null) {
            clearTimeout(this.#scrollSettleHandle);
        }
        this.#scrollSettleHandle = setTimeout((): void => {
            this.#normalize();
        }, SCROLL_SETTLE_MS);
    };

    protected readonly track: Signal<ElementRef<HTMLElement> | undefined> = viewChild<ElementRef<HTMLElement>>('track');

    // Render the list twice so wrap-around always has identical content to reveal.
    // The duplicate copy turns an abrupt edge jump into a continuous, seamless loop.
    protected readonly loopItems: Signal<readonly T[]> = computed((): readonly T[] => {
        const list: readonly T[] = this.items();
        return list.length > 1 ? [...list, ...list] : list;
    });

    public readonly items: InputSignal<readonly T[]> = input.required<readonly T[]>();
    public readonly itemTemplate: InputSignal<TemplateRef<unknown>> = input.required<TemplateRef<unknown>>();
    public readonly autoplayMs: InputSignal<number> = input<number>(DEFAULT_AUTOPLAY_MS);
    public readonly prevAriaLabel: InputSignal<string> = input<string>($localize`:@@carouselPrev:Назад`);
    public readonly nextAriaLabel: InputSignal<string> = input<string>($localize`:@@carouselNext:Вперёд`);

    constructor() {
        afterNextRender((): void => {
            this.track()?.nativeElement.addEventListener('scroll', this.#onScroll, { passive: true });
            this.#startAutoplay();
        });
        this.#destroyRef.onDestroy((): void => {
            this.track()?.nativeElement.removeEventListener('scroll', this.#onScroll);
            if (this.#scrollSettleHandle !== null) {
                clearTimeout(this.#scrollSettleHandle);
            }
            this.#stopAutoplay();
        });
    }

    protected onMouseEnter(): void {
        this.#hovered = true;
    }

    protected onMouseLeave(): void {
        this.#hovered = false;
    }

    protected onFocusIn(event: FocusEvent): void {
        // Pause only for keyboard focus (`:focus-visible`). A mouse click on an
        // arrow focuses the button too, but must not leave autoplay frozen until
        // focus happens to move elsewhere — hover already covers the mouse case.
        const target: HTMLElement | null = event.target as HTMLElement | null;
        this.#focused = (target as { matches?: (selector: string) => boolean } | null)?.matches?.(':focus-visible') ?? false;
    }

    protected onFocusOut(): void {
        this.#focused = false;
    }

    protected scroll(direction: -1 | 1): void {
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        const el: HTMLElement | undefined = this.track()?.nativeElement;
        if (!el) {
            return;
        }
        const step: number = Math.max(el.clientWidth * 0.8, 240);
        const copyWidth: number = this.#copyWidth(el);

        // Scrolling left from the start: pre-jump forward by one copy first
        // (invisible — the copies are identical), so the smooth scroll has the
        // tail of the list to reveal instead of hitting a wall. The smooth step
        // then plays out and #normalize re-aligns once it settles.
        if (direction === -1 && copyWidth > 0 && el.scrollLeft - step < 0) {
            el.scrollLeft += copyWidth;
        }
        el.scrollBy({ left: direction * step, behavior: 'smooth' });
    }

    #startAutoplay(): void {
        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }
        const interval: number = Math.max(MIN_AUTOPLAY_MS, this.autoplayMs());
        if (interval <= 0) {
            return;
        }
        // Respect user accessibility preference — no autoplay if motion is reduced.
        const reducedMotion: boolean =
            (globalThis as { matchMedia?: typeof globalThis.matchMedia }).matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        if (reducedMotion) {
            return;
        }
        this.#autoplayHandle = setInterval((): void => {
            if (this.#hovered || this.#focused) {
                return;
            }
            this.scroll(1);
        }, interval);
    }

    #stopAutoplay(): void {
        if (this.#autoplayHandle !== null) {
            clearInterval(this.#autoplayHandle);
            this.#autoplayHandle = null;
        }
    }

    #normalize(): void {
        const el: HTMLElement | undefined = this.track()?.nativeElement;
        if (!el) {
            return;
        }
        const copyWidth: number = this.#copyWidth(el);
        // Once past the first copy, slide back by exactly one copy. Because the
        // two copies are pixel-identical this is invisible — no jerk, just a
        // silent reset that gives the loop infinite runway in both directions.
        if (copyWidth > 0 && el.scrollLeft >= copyWidth) {
            el.scrollLeft -= copyWidth;
        }
    }

    // Distance between the two identical copies, i.e. the offset of the first
    // slide of the second copy. Measured from the DOM so flex gaps are accounted
    // for exactly — `scrollWidth / 2` would be off by half a gap and drift.
    #copyWidth(el: HTMLElement): number {
        const count: number = this.items().length;
        const slides: HTMLCollection = el.children;
        if (count < 2 || slides.length < count * 2) {
            return 0;
        }
        return (slides[count] as HTMLElement).offsetLeft - (slides[0] as HTMLElement).offsetLeft;
    }
}
